import { RadioGroup } from '@headlessui/react';
import { Fragment, useCallback, useEffect, useState } from 'react';

import { ANALYTIC_EVENTS, Analytics } from 'analytics/Analytics';
import { DeviceTypes } from 'constants/app';
import { ONLY_BANK_CARD_LIST, PAY_METHOD } from 'constants/payment';
import { SEARCH_PARAMS } from 'constants/search-params';
import { useIsMobile, useTranslate, useYM } from 'hooks';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useGetCalcComissMutation } from 'store/payment/service';
import { setAcquiring, setIsOnlyBankCard, setPaymentData } from 'store/payment/slice';
import type { AcquiringType } from 'types/payment';
import { detectDeviceType, getQueryParams } from 'utils';
import { ApplePay } from 'views/forms/base/apple-pay';
import { GooglePay } from 'views/forms/base/google-pay';

import { Button } from '../ui';

import { AcquiringsItem } from './acquirings-item';
import { AcquiringsRadioBtn } from './acquirings-radio-btn';

const AcquiringsCards = () => {
  const { paymentData, amount, isPayment, isInvoice, isOnlyBankCard } = useAppSelector(
    (state) => state.payment,
  );
  const [acquiringFormType, setAcquiringFormType] = useState(paymentData?.acquirings[0]?.ID || 0);
  const { isMobile } = useIsMobile();
  const { t } = useTranslate();

  const [calcCommission, { isLoading }] = useGetCalcComissMutation();
  const dispatch = useAppDispatch();
  const { trackOnClick } = useYM();

  useEffect(() => {
    trackOnClick('openPaymentChoice');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const bankCardBool =
      (paymentData?.fix &&
        !paymentData.requiredDescription &&
        paymentData?.acquirings.every((item) => ONLY_BANK_CARD_LIST.includes(item.formType))) ??
      false;

    dispatch(setIsOnlyBankCard(bankCardBool));
  }, [dispatch, paymentData?.acquirings, paymentData?.fix, paymentData?.requiredDescription]);

  const handleSelectAcquiring = useCallback(
    (item: AcquiringType) => {
      if (paymentData && isInvoice) {
        const payload = {
          ID: paymentData?.ID,
          amount: Math.abs(+amount) * 100,
          acquiringID: item.ID,
        };

        trackOnClick(item.title);
        calcCommission(payload)
          .unwrap()
          .then((res) => {
            Analytics.sentEvent(ANALYTIC_EVENTS.getCommissionSuccess);
            dispatch(setAcquiring(item));
            dispatch(
              setPaymentData({
                ...paymentData,
                ...res,
              }),
            );
          })
          .catch((error) => {
            Analytics.sentEvent(ANALYTIC_EVENTS.getCommissionError, {
              transaction_id: getQueryParams(SEARCH_PARAMS.ID),
              message: JSON.stringify(error.data),
            });
          });
      } else {
        dispatch(setAcquiring(item));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [amount, calcCommission, dispatch, isInvoice, paymentData],
  );

  useEffect(() => {
    if (isOnlyBankCard && paymentData?.acquirings) {
      const bankCardItem: AcquiringType = paymentData?.acquirings[0];

      handleSelectAcquiring(bankCardItem);
    }
  }, [handleSelectAcquiring, isOnlyBankCard, paymentData?.acquirings]);

  const renderCard = (item: AcquiringType) => {
    if (item.formType === PAY_METHOD.GOOGLE_PAY) {
      return <GooglePay acquiringID={item.ID} />;
    }

    if (item.formType === PAY_METHOD.APPLE_PAY) {
      return <ApplePay acquiringID={item.ID} />;
    }

    return (
      <AcquiringsItem
        key={item.ID}
        acquiringsItem={item}
        paymentData={paymentData}
        handleSelectAcquiring={handleSelectAcquiring}
        isLoading={isLoading}
      />
    );
  };

  const renderButton = () => {
    const item = paymentData?.acquirings?.find(
      (acquiringItem) => acquiringItem.ID === acquiringFormType,
    );

    if (!item) {
      return null;
    }

    if (item.formType === PAY_METHOD.GOOGLE_PAY) {
      return <GooglePay acquiringID={item.ID} />;
    }

    if (item.formType === PAY_METHOD.APPLE_PAY) {
      return <ApplePay acquiringID={item.ID} />;
    }

    return (
      <Button onClick={() => handleSelectAcquiring(item)}>
        {isPayment ? t('1xbet.pay') : t('1xbet.payout')}
      </Button>
    );
  };

  const renderRadioBtn = (item: AcquiringType, checked: boolean) => {
    const itemAquiringBtn = (
      <AcquiringsRadioBtn checked={checked} acquiringsItem={item} paymentData={paymentData} />
    );

    if (item.formType === PAY_METHOD.GOOGLE_PAY) {
      return detectDeviceType() === DeviceTypes.ANDROID ||
        detectDeviceType() === DeviceTypes.OTHER ? (
        itemAquiringBtn
      ) : (
        <div />
      );
    }

    if (item.formType === PAY_METHOD.APPLE_PAY) {
      return detectDeviceType() === DeviceTypes.IOS ? itemAquiringBtn : <div />;
    }

    return itemAquiringBtn;
  };

  return !isOnlyBankCard ? (
    <div className="w-full md:w-auto flex-grow md:flex-grow-0 content-end">
      <h3 className="font-bold md:font-semibold text-base text-center md:text-left leading-6 md:leading-4 mb-4 md:mb-6">
        {isPayment ? t('lang.pay_methods') : t('lang.payout_methods')}
      </h3>
      {isMobile ? (
        <div>
          <RadioGroup value={acquiringFormType} onChange={setAcquiringFormType}>
            {paymentData?.acquirings.map((item) => (
              <RadioGroup.Option key={item.ID} value={item.ID} className="mb-2 last:mb-0">
                {({ checked }) => renderRadioBtn(item, checked)}
              </RadioGroup.Option>
            ))}
          </RadioGroup>
          <div className="grid grid-cols-1 h-10 mt-4">{renderButton()}</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {paymentData?.acquirings.map((item) => (
            <Fragment key={item.ID}>{renderCard(item)}</Fragment>
          ))}
        </div>
      )}
    </div>
  ) : (
    <div />
  );
};

export { AcquiringsCards };
